import React, { useEffect, useMemo, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { apiFetch } from '../../services/api/client';
import { DEFAULT_ARTIST_ID } from '../../config/nova-constants';
import {
  getCategoryAxisLayout,
  maxGridBottomPct,
  splitWordsOnSpace,
} from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

type EngagementRatesChartProps = {
  className?: string;
  refreshKey?: number;
  artistId?: string;
  size?: 'compact' | 'full';
};

type SeriesEntry = {
  platform: string;
  rate: number;
  change: number;
  color: string;
  chartRange?: number;
};

const colorPalette = ['#0EA5E9', '#F97316', '#8B5CF6', '#22C55E', '#F43F5E'];

const EngagementRatesChart: React.FC<EngagementRatesChartProps> = ({
  className = '',
  refreshKey = 0,
  artistId,
  size = 'compact'
}) => {
  const [seriesData, setSeriesData] = useState<SeriesEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const id = artistId || DEFAULT_ARTIST_ID;
        const response = await apiFetch(`/analytics/artist/engagement-rates?artist_id=${id}`);
        const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];

        const normalized = items
          .map((item: any, index: number) => ({
            platform: item.platform || item.name || `Platform ${index + 1}`,
            rate: Number(item.engagementRate ?? item.rate ?? item.value ?? 0),
            change: Number(item.change ?? item.delta ?? 0),
            chartRange: Number(item.chartRange ?? item.max ?? item.maxRange ?? 0),
            color: item.color || colorPalette[index % colorPalette.length]
          }))
          .filter((entry: SeriesEntry) => !Number.isNaN(entry.rate))
          .slice(0, 3); // always show at most three platforms

        if (isMounted) {
          setSeriesData(normalized);
        }
      } catch (_error) {
        if (isMounted) {
          setSeriesData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [refreshKey, artistId]);

  const option = useMemo<EChartsOption>(() => {
    // Use the highest chartRange value from API data only (no padding)
    const maxChartRange = Math.max(...seriesData.map(item => item.chartRange || 0), 0);
    // If no chartRange provided, fall back to max rate value
    const maxRate = Math.max(...seriesData.map(item => item.rate || 0), 0);
    // Use chartRange if available, otherwise use rate, but ensure minimum of 100 if values are high
    const maxValueRaw = maxChartRange > 0 ? maxChartRange : (maxRate >= 90 ? 100 : maxRate);
    // Guard against all-zero datasets so the axis still renders
    const maxValue = maxValueRaw > 0 ? maxValueRaw : 10;
    const xLayout = getCategoryAxisLayout(seriesData.length);

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        confine: true,
        position: function (point: any, params: any, dom: any, rect: any, size: any) {
          // Use relative positioning if any required parameter is null
          if (!point || !size || !size.contentSize || !size.viewSize) {
            return [point?.[0] ? point[0] + 20 : 20, point?.[1] || 0];
          }
          
          // For horizontal bar charts, calculate relative position
          const tooltipHeight = size.contentSize[1] || 0;
          const tooltipWidth = size.contentSize[0] || 0;
          const pointX = point[0];
          const pointY = point[1];
          
          const chartHeight = size.viewSize[1] || 0;
          const chartWidth = size.viewSize[0] || 0;
          
          // Position tooltip to the right of the bar
          let tooltipX = pointX + 20;
          let tooltipY = pointY;
          
          // If tooltip would be cut off at bottom, position it above the bar
          if (tooltipHeight > 0 && pointY + tooltipHeight > chartHeight - 30) {
            tooltipY = pointY - tooltipHeight - 15;
          }
          
          // Ensure tooltip doesn't go off the right edge
          if (tooltipWidth > 0 && tooltipX + tooltipWidth > chartWidth - 20) {
            tooltipX = pointX - tooltipWidth - 20;
          }
          
          return [tooltipX, tooltipY];
        },
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        formatter: function(params: any) {
          const param = params[0];
          const data = param.data;
          
          // Get the color from the series data
          const seriesEntry = seriesData.find(item => item.platform === data.platform);
          const barColor = seriesEntry?.color || '#0EA5E9';
          
          let tooltipHtml = `<div style="font-weight: 600; margin-bottom: 8px; color: #111827;">${data.platform}</div>`;
          
          // Show engagement rate with colored bullet point
          tooltipHtml += `
            <div style="color: ${barColor}; margin-bottom: 4px;">
              ● Engagement Rate: ${data.value.toFixed(2)}%
            </div>
          `;
          
          return tooltipHtml;
        }
      },
      grid: {
        left: '6%',
        right: '4%',
        top: '8%',
        bottom: maxGridBottomPct('12%', xLayout.gridBottom),
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: seriesData.map(item => item.platform),
        axisLine: {
          show: true,
          lineStyle: {
            color: '#d1d5db',
            width: 1
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          lineHeight: 13,
          formatter: (value: string) => splitWordsOnSpace(value, 11)
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: true,
          lineStyle: {
            color: '#d1d5db',
            width: 1
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: 10,
          formatter: (value: number) => `${value}%`
        },
        min: 0,
        max: maxValue,
        splitLine: {
          lineStyle: {
            color: '#e5e7eb'
          }
        }
      },
      series: [
        {
          type: 'bar',
          data: seriesData.map(item => ({
            value: item.rate,
            platform: item.platform,
            change: item.change,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: `${item.color}1A` },
                  { offset: 0.35, color: `${item.color}99` },
                  { offset: 1, color: item.color }
                ]
              },
              shadowBlur: 6,
              shadowColor: `${item.color}40`,
              borderRadius: [6, 6, 0, 0]
            }
          })),
          barWidth: 30,
          label: {
            show: true,
            position: 'top',
            distance: 6,
            formatter: ({ value }: any) => `${Number(value || 0).toFixed(2)}%`,
            color: '#111827',
            fontWeight: 600,
            fontSize: 10
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          animationDelay: function (idx: number) {
            return idx * 50;
          }
        }
      ]
    };
  }, [refreshKey, seriesData, artistId]);

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (!seriesData || seriesData.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-sm">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  const containerStyle = size === 'compact'
    ? { maxHeight: 400 }
    : { minHeight: 460 };

  const chartStyle = size === 'compact'
    ? { width: '100%', height: '100%', maxWidth: 440, maxHeight: 380 }
    : { width: '100%', height: '100%', maxWidth: 1100, maxHeight: 720 };

  return (
    <div
      className={`w-full h-full bg-transparent px-2 pt-2 pb-1 ${className || ''}`}
      style={containerStyle}
    >
      <BaseChart
        option={option}
        loading={false}
        className="w-full h-full"
        style={chartStyle}
      />
    </div>
  );
};

export default EngagementRatesChart;
